import { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { toast } from "sonner";
import {
  MessageCircle,
  X,
  Send,
  Loader2,
  Sparkles,
  Maximize2,
  Minimize2,
  ExternalLink,
} from "lucide-react";
import { useFarmaciaAuth } from "@/context/FarmaciaAuthContext";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const WELCOME_MESSAGE = {
  role: "assistant",
  content:
    "Ciao! Sono l'assistente IA del Portale Farmacia Affidya. Posso aiutarti con Carepy, controllo ricette, solleciti, clienti diretti e impostazioni. Cosa ti serve?",
};

const SUGGESTED_PROMPTS = [
  "Come carico un PDF Carepy?",
  "Come funziona il controllo ricette?",
  "Come creo un cliente diretto?",
  "Mostra il riepilogo KPI",
];

function AssistantMarkdown({ children }) {
  return (
    <ReactMarkdown
      remarkPlugins={[remarkGfm]}
      components={{
        p: ({ children: c }) => <p className="mb-2 last:mb-0">{c}</p>,
        ul: ({ children: c }) => <ul className="list-disc pl-4 mb-2 space-y-0.5">{c}</ul>,
        ol: ({ children: c }) => <ol className="list-decimal pl-4 mb-2 space-y-0.5">{c}</ol>,
        li: ({ children: c }) => <li className="leading-snug">{c}</li>,
        strong: ({ children: c }) => <strong className="font-semibold">{c}</strong>,
        a: ({ href, children: c }) => {
          const safe =
            typeof href === "string" && /^(https?:|mailto:|tel:)/i.test(href.trim())
              ? href.trim()
              : undefined;
          return (
            <a
              href={safe}
              className="underline font-medium"
              target={safe ? "_blank" : undefined}
              rel="noopener noreferrer"
            >
              {c}
            </a>
          );
        },
        code: ({ inline, children: c }) =>
          inline ? (
            <code className="px-1 py-0.5 rounded bg-black/10 text-[0.85em]">{c}</code>
          ) : (
            <code className="block my-2 p-2 rounded bg-black/10 text-[0.85em] overflow-x-auto whitespace-pre-wrap">
              {c}
            </code>
          ),
      }}
    >
      {children}
    </ReactMarkdown>
  );
}

/**
 * Assistente AI dedicato al Portale Farmacia.
 * Usa /api/farmacia/assistant/* e la knowledge base KNOWLEDGE_BASE_FARMACIA.md.
 */
export default function FarmaciaAssistantChatWidget({ sidebarCollapsed = false }) {
  const { user, token } = useFarmaciaAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [expanded, setExpanded] = useState(true);
  const [available, setAvailable] = useState(null);
  const [messages, setMessages] = useState([WELCOME_MESSAGE]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const bottomRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    if (!token) {
      setAvailable(false);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const res = await axios.get(`${API_URL}/api/farmacia/assistant/status`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!cancelled) setAvailable(!!res.data?.available);
      } catch {
        if (!cancelled) setAvailable(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [token]);

  useEffect(() => {
    const onOpenRequest = () => {
      if (available === false) {
        toast.error("Assistente non disponibile al momento.");
        return;
      }
      setExpanded(true);
      setOpen(true);
    };
    window.addEventListener("farmacia-assistant-open", onOpenRequest);
    return () => window.removeEventListener("farmacia-assistant-open", onOpenRequest);
  }, [available]);

  useEffect(() => {
    if (open && bottomRef.current) {
      bottomRef.current.scrollIntoView({ behavior: "smooth" });
    }
  }, [messages, open, loading]);

  useEffect(() => {
    if (open && inputRef.current) {
      setTimeout(() => inputRef.current?.focus(), 100);
    }
  }, [open]);

  useEffect(() => {
    if (!open) setExpanded(true);
  }, [open]);

  const sendMessage = async (overrideText) => {
    const text = (overrideText ?? input).trim();
    if (!text || loading) return;

    const userMessage = { role: "user", content: text };
    const nextMessages = [...messages, userMessage];
    setMessages(nextMessages);
    setInput("");
    setLoading(true);

    try {
      const payload = nextMessages
        .filter((m) => m.role === "user" || m.role === "assistant")
        .map((m) => ({ role: m.role, content: m.content }));

      const res = await axios.post(
        `${API_URL}/api/farmacia/assistant/chat`,
        { messages: payload },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      const reply = res.data?.message || "Nessuna risposta ricevuta.";
      const actions = res.data?.suggested_actions || [];
      setMessages((prev) => [
        ...prev,
        { role: "assistant", content: reply, actions },
      ]);

      if (res.data?.disabled) setAvailable(false);
    } catch (err) {
      const detail = err.response?.data?.detail;
      const msg =
        typeof detail === "string"
          ? detail
          : "Errore di comunicazione con l'assistente. Riprova.";
      toast.error(msg);
      setMessages((prev) => [
        ...prev,
        {
          role: "assistant",
          content:
            "Mi dispiace, non sono riuscito a rispondere. Consulta la Guida Portale Farmacia dalle Impostazioni.",
        },
      ]);
    } finally {
      setLoading(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const showWelcomeExtras =
    messages.length === 1
    && messages[0].role === "assistant"
    && messages[0].content === WELCOME_MESSAGE.content;

  if (available === false) return null;

  const panelClassName = expanded
    ? "fixed inset-y-0 right-0 z-[200] w-[min(100vw,540px)] flex flex-col border-l border-slate-200 bg-white shadow-[-8px_0_32px_rgba(15,23,42,0.12)]"
    : "fixed bottom-20 right-6 z-[100] w-[min(100vw-2rem,400px)] h-[min(70vh,520px)] flex flex-col rounded-2xl border border-slate-200 bg-white shadow-2xl overflow-hidden";

  return (
    <>
      {open && (
        <div className={panelClassName} data-testid="farmacia-assistant-chat-panel">
          <div className="flex items-center justify-between px-4 py-3 bg-gradient-to-r from-teal-600 to-cyan-600 text-white flex-shrink-0">
            <div className="flex items-center gap-2 min-w-0">
              <div className="w-8 h-8 rounded-full bg-white/20 flex items-center justify-center flex-shrink-0">
                <Sparkles className="w-4 h-4" />
              </div>
              <div className="min-w-0">
                <p className="text-sm font-semibold truncate">Assistente IA Farmacia</p>
                <p className="text-[10px] text-white/80 truncate">{user?.nome || "Portale Farmacia"}</p>
              </div>
            </div>
            <div className="flex items-center gap-1">
              <button
                type="button"
                onClick={() => setExpanded((v) => !v)}
                className="p-1.5 rounded-lg hover:bg-white/20 transition-colors"
                aria-label={expanded ? "Riduci pannello" : "Ingrandisci pannello"}
              >
                {expanded ? <Minimize2 className="w-4 h-4" /> : <Maximize2 className="w-4 h-4" />}
              </button>
              <button
                type="button"
                onClick={() => setOpen(false)}
                className="p-1.5 rounded-lg hover:bg-white/20 transition-colors"
                aria-label="Chiudi assistente"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          </div>

          <div className="flex-1 overflow-y-auto px-3 py-3 space-y-3 bg-slate-50">
            {messages.map((m, i) => (
              <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                <div
                  className={`max-w-[90%] rounded-2xl px-3.5 py-2.5 text-sm leading-relaxed ${
                    m.role === "user"
                      ? "bg-teal-600 text-white rounded-br-md"
                      : "bg-white border border-slate-200 text-slate-800 rounded-bl-md shadow-sm"
                  }`}
                >
                  {m.role === "assistant" ? (
                    <AssistantMarkdown>{m.content}</AssistantMarkdown>
                  ) : (
                    <p className="whitespace-pre-wrap">{m.content}</p>
                  )}
                  {m.actions?.length > 0 && (
                    <div className="mt-2 flex flex-wrap gap-1.5">
                      {m.actions.map((a, j) => (
                        <button
                          key={j}
                          type="button"
                          onClick={() => {
                            if (a.path) {
                              navigate(a.path);
                              setOpen(false);
                            }
                          }}
                          className="inline-flex items-center gap-1 text-xs px-2.5 py-1 rounded-full bg-teal-50 text-teal-800 border border-teal-200 hover:bg-teal-100"
                        >
                          <ExternalLink className="w-3 h-3" />
                          {a.label || a.path}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            ))}

            {showWelcomeExtras && (
              <div className="flex flex-wrap gap-1.5 pt-1">
                {SUGGESTED_PROMPTS.map((p) => (
                  <button
                    key={p}
                    type="button"
                    onClick={() => sendMessage(p)}
                    className="text-xs px-2.5 py-1.5 rounded-full bg-white border border-slate-200 text-slate-700 hover:border-teal-300 hover:text-teal-800"
                  >
                    {p}
                  </button>
                ))}
              </div>
            )}

            {loading && (
              <div className="flex justify-start">
                <div className="bg-white border border-slate-200 rounded-2xl rounded-bl-md px-3.5 py-2.5 shadow-sm">
                  <Loader2 className="w-4 h-4 animate-spin text-teal-600" />
                </div>
              </div>
            )}
            <div ref={bottomRef} />
          </div>

          <div className="flex-shrink-0 border-t border-slate-200 bg-white p-3">
            <div className="flex items-end gap-2">
              <textarea
                ref={inputRef}
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={handleKeyDown}
                rows={1}
                placeholder="Scrivi una domanda…"
                className="flex-1 resize-none rounded-xl border border-slate-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-teal-500/40 max-h-28"
                disabled={loading}
              />
              <button
                type="button"
                onClick={() => sendMessage()}
                disabled={loading || !input.trim()}
                className="p-2.5 rounded-xl bg-teal-600 text-white hover:bg-teal-700 disabled:opacity-40"
                aria-label="Invia"
              >
                <Send className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>
      )}

      {!open && (
        <div
          className={`fixed bottom-3 right-3 z-[100] flex h-14 w-14 items-center justify-center md:bottom-4 md:right-4 ${
            sidebarCollapsed ? "" : "lg:hidden"
          }`}
        >
          <span className="pointer-events-none absolute h-14 w-14 rounded-2xl bg-[#D9E0EA]" aria-hidden />
          <button
            type="button"
            onClick={() => {
              setExpanded(true);
              setOpen(true);
            }}
            className="relative flex h-12 w-12 items-center justify-center rounded-2xl bg-teal-600 text-white shadow-sm transition-colors hover:bg-teal-700"
            data-testid="farmacia-assistant-chat-fab"
            aria-label="Apri assistente IA farmacia"
            title="Assistente IA"
          >
            <MessageCircle className="h-5 w-5" />
          </button>
        </div>
      )}
    </>
  );
}
